/**
 * Member Settings Page - Olympic Fit Wellness
 * Connected to live Supabase APIs
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  User, 
  Mail, 
  Phone,
  Bell,
  Lock,
  Shield,
  Camera,
  Save,
  Check,
  AlertCircle,
  LogOut,
  Eye,
  EyeOff,
  Loader2
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';

// Security Tab Component
function SecurityTab() {
  const { updatePassword, signOut } = useAuth();
  const navigate = useNavigate();
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState(null);
  const [passwordSuccess, setPasswordSuccess] = useState(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError(null);
    setPasswordSuccess(null);

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    if (passwordForm.newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      return;
    }

    if (!passwordForm.currentPassword) {
      setPasswordError('Current password is required');
      return;
    }

    setIsChangingPassword(true);
    try {
      const result = await updatePassword(passwordForm.currentPassword, passwordForm.newPassword);
      if (result.success) {
        setPasswordSuccess('Password updated successfully!');
        setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setShowChangePassword(false);
      } else {
        setPasswordError(result.error || 'Failed to update password');
      }
    } catch (err) {
      setPasswordError(err.message || 'Failed to update password');
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await signOut();
      navigate('/login');
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setIsLoggingOut(false);
    }
  };

  const handleEnable2FA = async () => {
    try {
      const result = await api.enable2FA();
      alert(result.message || '2FA setup initiated');
    } catch (err) {
      alert(err.message || 'Failed to enable 2FA');
    }
  };

  return (
    <>
      <div className="p-6 border-b border-neutral-100">
        <h2 className="text-xl font-heading font-bold text-neutral-900">Security Settings</h2>
        <p className="text-neutral-500 text-sm">Manage your account security</p>
      </div>
      
      <div className="p-6 space-y-6">
        {/* Password Success/Error Messages */}
        {passwordError && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            {passwordError}
          </div>
        )}
        {passwordSuccess && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2">
            <Check className="w-5 h-5" />
            {passwordSuccess}
          </div>
        )}

        {/* Change Password */}
        <div className="p-4 bg-neutral-50 rounded-lg">
          <h4 className="font-medium text-neutral-900 mb-2">Change Password</h4>
          <p className="text-sm text-neutral-500 mb-4">Update your password regularly for security</p>
          
          {!showChangePassword ? (
            <button 
              onClick={() => setShowChangePassword(true)}
              className="px-4 py-2 bg-neutral-900 text-white text-sm font-medium rounded-lg hover:bg-neutral-800"
            >
              Change Password
            </button>
          ) : (
            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Current Password</label>
                <div className="relative">
                  <input
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={passwordForm.currentPassword}
                    onChange={(e) => setPasswordForm({...passwordForm, currentPassword: e.target.value})}
                    className="w-full px-4 py-2 border border-neutral-200 rounded-lg pr-10"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400"
                  >
                    {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">New Password</label>
                <div className="relative">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm({...passwordForm, newPassword: e.target.value})}
                    className="w-full px-4 py-2 border border-neutral-200 rounded-lg pr-10"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400"
                  >
                    {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Confirm New Password</label>
                <input
                  type="password"
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm({...passwordForm, confirmPassword: e.target.value})}
                  className="w-full px-4 py-2 border border-neutral-200 rounded-lg"
                  required
                />
              </div>
              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={isChangingPassword}
                  className="px-4 py-2 bg-[#3B82F6] text-white text-sm font-medium rounded-lg hover:bg-blue-600 disabled:opacity-50 flex items-center gap-2"
                >
                  {isChangingPassword && <Loader2 className="w-4 h-4 animate-spin" />}
                  {isChangingPassword ? 'Updating...' : 'Update Password'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowChangePassword(false)}
                  className="px-4 py-2 bg-neutral-200 text-neutral-700 text-sm font-medium rounded-lg hover:bg-neutral-300"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
        
        {/* Two-Factor Authentication */}
        <div className="p-4 bg-neutral-50 rounded-lg">
          <h4 className="font-medium text-neutral-900 mb-2">Two-Factor Authentication</h4>
          <p className="text-sm text-neutral-500 mb-4">Add an extra layer of security to your account</p>
          <button 
            onClick={handleEnable2FA}
            className="px-4 py-2 bg-[#3B82F6] text-white text-sm font-medium rounded-lg hover:bg-blue-600"
          >
            Enable 2FA
          </button>
        </div>

        {/* Logout */}
        <div className="p-4 bg-neutral-50 rounded-lg">
          <h4 className="font-medium text-neutral-900 mb-2">Sign Out</h4>
          <p className="text-sm text-neutral-500 mb-4">Sign out of your account on this device</p>
          <button 
            onClick={handleLogout}
            disabled={isLoggingOut}
            className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
          >
            {isLoggingOut ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogOut className="w-4 h-4" />}
            {isLoggingOut ? 'Signing out...' : 'Sign Out'}
          </button>
        </div>
      </div>
    </>
  );
}

export default function SettingsPage() {
  const { profile, user, refreshProfile } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [isSaving, setIsSaving] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    emergency_contact: '',
    emergency_phone: ''
  });

  const [notifications, setNotifications] = useState({
    class_reminders: true,
    booking_confirmations: true,
    promotional_emails: false,
    newsletter: true,
    sms_notifications: false
  });

  // Load profile data
  useEffect(() => {
    if (profile) {
      // Handle address - could be string or object
      let addressStr = '';
      let cityStr = '';
      let stateStr = '';
      let zipStr = '';
      
      if (profile.address) {
        if (typeof profile.address === 'object') {
          addressStr = profile.address.street || profile.address.line1 || '';
          cityStr = profile.address.city || '';
          stateStr = profile.address.state || '';
          zipStr = profile.address.zip || profile.address.zip_code || '';
        } else {
          addressStr = profile.address;
        }
      }
      cityStr = cityStr || profile.city || '';
      stateStr = stateStr || profile.state || '';
      zipStr = zipStr || profile.zip_code || '';

      // Handle emergency contact - could be string or object
      let emergencyName = '';
      let emergencyPhone = '';
      
      if (profile.emergency_contact) {
        if (typeof profile.emergency_contact === 'object') {
          emergencyName = profile.emergency_contact.name || '';
          emergencyPhone = profile.emergency_contact.phone || '';
        } else {
          emergencyName = profile.emergency_contact;
        }
      }
      emergencyPhone = emergencyPhone || profile.emergency_phone || '';

      setFormData({
        full_name: profile.full_name || '',
        email: user?.email || '',
        phone: profile.phone || '',
        address: addressStr,
        city: cityStr,
        state: stateStr,
        zip_code: zipStr,
        emergency_contact: emergencyName,
        emergency_phone: emergencyPhone
      });
    }
  }, [profile, user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleNotificationChange = (key) => {
    setNotifications(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await api.updateMyProfile({
        full_name: formData.full_name,
        phone: formData.phone,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        zip_code: formData.zip_code,
        emergency_contact: formData.emergency_contact,
        emergency_phone: formData.emergency_phone
      });
      setSuccess('Profile updated successfully!');
      // Refresh profile in context
      if (refreshProfile) refreshProfile();
    } catch (err) {
      console.error('Save error:', err);
      setError(err.message || 'Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };

  const tabs = [
    { id: 'profile', name: 'Profile', icon: User },
    { id: 'notifications', name: 'Notifications', icon: Bell },
    { id: 'security', name: 'Security', icon: Lock },
    { id: 'privacy', name: 'Privacy', icon: Shield }
  ];

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-heading font-bold text-neutral-900">Settings</h1>
        <p className="text-neutral-500">Manage your account and preferences</p>
      </div>

      {/* Alerts */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <Check className="w-5 h-5" />
          {success}
        </div>
      )}

      <div className="grid lg:grid-cols-4 gap-6">
        {/* Sidebar */}
        <div className="bg-white rounded-xl shadow-sm p-4">
          <nav className="space-y-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  activeTab === tab.id
                    ? 'bg-[#3B82F6]/10 text-[#3B82F6]'
                    : 'text-neutral-600 hover:bg-neutral-100'
                }`}
              >
                <tab.icon className="w-5 h-5" />
                <span className="font-medium">{tab.name}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="lg:col-span-3 bg-white rounded-xl shadow-sm">
          {activeTab === 'profile' && (
            <>
              <div className="p-6 border-b border-neutral-100">
                <h2 className="text-xl font-heading font-bold text-neutral-900">Profile Information</h2>
                <p className="text-neutral-500 text-sm">Update your personal information</p>
              </div>
              
              <div className="p-6 space-y-6">
                {/* Avatar */}
                <div className="flex items-center gap-6">
                  <div className="relative">
                    {profile?.avatar_url ? (
                      <img src={profile.avatar_url} alt="" className="w-24 h-24 rounded-full object-cover" />
                    ) : (
                      <div className="w-24 h-24 rounded-full bg-[#3B82F6] flex items-center justify-center">
                        <span className="text-3xl font-bold text-white">
                          {formData.full_name?.[0] || 'U'}
                        </span>
                      </div>
                    )}
                    <button className="absolute bottom-0 right-0 w-8 h-8 bg-white border border-neutral-200 rounded-full flex items-center justify-center shadow-sm hover:bg-neutral-50">
                      <Camera className="w-4 h-4 text-neutral-600" />
                    </button>
                  </div>
                  <div>
                    <h3 className="font-semibold text-neutral-900">{formData.full_name || 'Your Name'}</h3>
                    <p className="text-sm text-neutral-500">{formData.email}</p>
                    <button className="mt-2 text-sm text-[#3B82F6] font-medium hover:underline">
                      Change photo
                    </button>
                  </div>
                </div>

                {/* Form Fields */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">Full Name</label>
                    <input
                      type="text"
                      name="full_name"
                      value={formData.full_name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-neutral-200 rounded-lg focus:outline-none focus:border-[#3B82F6]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      disabled
                      className="w-full px-4 py-3 border border-neutral-200 rounded-lg bg-neutral-50 text-neutral-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-neutral-200 rounded-lg focus:outline-none focus:border-[#3B82F6]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">Address</label>
                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-neutral-200 rounded-lg focus:outline-none focus:border-[#3B82F6]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">City</label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-neutral-200 rounded-lg focus:outline-none focus:border-[#3B82F6]"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">State</label>
                      <input
                        type="text"
                        name="state"
                        value={formData.state}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-neutral-200 rounded-lg focus:outline-none focus:border-[#3B82F6]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">ZIP</label>
                      <input
                        type="text"
                        name="zip_code"
                        value={formData.zip_code}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-neutral-200 rounded-lg focus:outline-none focus:border-[#3B82F6]"
                      />
                    </div>
                  </div>
                </div>

                {/* Emergency Contact */}
                <div className="pt-6 border-t border-neutral-100">
                  <h3 className="font-semibold text-neutral-900 mb-4">Emergency Contact</h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">Contact Name</label>
                      <input
                        type="text"
                        name="emergency_contact"
                        value={formData.emergency_contact}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-neutral-200 rounded-lg focus:outline-none focus:border-[#3B82F6]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">Contact Phone</label>
                      <input
                        type="tel"
                        name="emergency_phone"
                        value={formData.emergency_phone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-neutral-200 rounded-lg focus:outline-none focus:border-[#3B82F6]"
                      />
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="p-6 border-t border-neutral-100 flex justify-end">
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="inline-flex items-center gap-2 bg-[#3B82F6] text-white font-semibold px-6 py-3 text-sm hover:bg-blue-600 transition-colors disabled:opacity-50"
                >
                  {isSaving ? 'Saving...' : <><Save className="w-4 h-4" /> Save Changes</>}
                </button>
              </div>
            </>
          )}

          {activeTab === 'notifications' && (
            <>
              <div className="p-6 border-b border-neutral-100">
                <h2 className="text-xl font-heading font-bold text-neutral-900">Notification Preferences</h2>
                <p className="text-neutral-500 text-sm">Choose how you want to be notified</p>
              </div>
              
              <div className="p-6 space-y-6">
                {[
                  { key: 'class_reminders', title: 'Class Reminders', desc: 'Get reminded before your booked classes' },
                  { key: 'booking_confirmations', title: 'Booking Confirmations', desc: 'Receive confirmation when you book a class' },
                  { key: 'promotional_emails', title: 'Promotional Emails', desc: 'Receive special offers and promotions' },
                  { key: 'newsletter', title: 'Newsletter', desc: 'Weekly wellness tips and updates' },
                  { key: 'sms_notifications', title: 'SMS Notifications', desc: 'Receive important updates via text message' }
                ].map((item) => (
                  <div key={item.key} className="flex items-center justify-between p-4 bg-neutral-50 rounded-lg">
                    <div>
                      <h4 className="font-medium text-neutral-900">{item.title}</h4>
                      <p className="text-sm text-neutral-500">{item.desc}</p>
                    </div>
                    <button
                      onClick={() => handleNotificationChange(item.key)}
                      className={`w-12 h-6 rounded-full transition-colors ${
                        notifications[item.key] ? 'bg-[#3B82F6]' : 'bg-neutral-300'
                      }`}
                    >
                      <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${
                        notifications[item.key] ? 'translate-x-6' : 'translate-x-0.5'
                      }`} />
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}

          {activeTab === 'security' && (
            <SecurityTab />
          )}

          {activeTab === 'privacy' && (
            <>
              <div className="p-6 border-b border-neutral-100">
                <h2 className="text-xl font-heading font-bold text-neutral-900">Privacy Settings</h2>
                <p className="text-neutral-500 text-sm">Control your data and privacy</p>
              </div>
              
              <div className="p-6 space-y-6">
                <div className="p-4 bg-neutral-50 rounded-lg">
                  <h4 className="font-medium text-neutral-900 mb-2">Download Your Data</h4>
                  <p className="text-sm text-neutral-500 mb-4">Get a copy of all your personal data</p>
                  <button className="px-4 py-2 bg-neutral-900 text-white text-sm font-medium rounded-lg hover:bg-neutral-800">
                    Request Data
                  </button>
                </div>
                
                <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                  <h4 className="font-medium text-red-600 mb-2">Delete Account</h4>
                  <p className="text-sm text-neutral-600 mb-4">Permanently delete your account and all data.</p>
                  <button className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700">
                    Delete Account
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
